import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/lib/auth";
import { useLang } from "@/lib/lang";
import { Gift, Loader2, CheckCircle2 } from "lucide-react";

const PENDING_REFERRAL_KEY = "vivoz_pending_referral";

export default function ReferralLanding() {
    const { code } = useParams();
    const { t } = useLang();
    const { user, loading: authLoading } = useAuth();
    const nav = useNavigate();
    const [status, setStatus] = useState("checking"); // checking | redeeming | done | error
    const [error, setError] = useState("");

    useEffect(() => {
        if (authLoading) return;

        if (!user) {
            // Remember the code across the login/register redirect so we
            // can finish the redemption once the user comes back authenticated.
            sessionStorage.setItem(PENDING_REFERRAL_KEY, code);
            setStatus("checking");
            return;
        }

        sessionStorage.removeItem(PENDING_REFERRAL_KEY);
        setStatus("redeeming");
        api.post(`/referrals/redeem/${code}`)
            .then(() => setStatus("done"))
            .catch((err) => {
                setError(err.response?.data?.detail || t("referral.invalid"));
                setStatus("error");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, authLoading, code]);

    return (
        <div className="min-h-screen bg-white text-slate-900">
            <Navbar />
            <div className="max-w-md mx-auto px-6 pt-24 pb-24 text-center">
                <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-6">
                    {status === "done" ? (
                        <CheckCircle2 size={28} className="text-brand" />
                    ) : (
                        <Gift size={28} className="text-brand" />
                    )}
                </div>

                {(status === "checking" || status === "redeeming") && (
                    <>
                        <p className="text-slate-700 mb-2">{t("referral.landing_invites_you")}</p>
                        <div className="flex items-center justify-center gap-2 text-slate-500 mb-8" data-testid="referral-landing-loading">
                            <Loader2 size={16} className="animate-spin" />
                            {status === "redeeming" ? t("referral.redeeming") : t("referral.checking")}
                        </div>
                        {!user && !authLoading && (
                            <div className="flex flex-col gap-3">
                                <Link to="/login" className="btn-primary w-full justify-center" data-testid="referral-landing-login-btn">
                                    {t("referral.login_cta")}
                                </Link>
                                <Link to="/register" className="btn-ghost w-full justify-center" data-testid="referral-landing-register-btn">
                                    {t("referral.register_cta")}
                                </Link>
                            </div>
                        )}
                    </>
                )}

                {status === "done" && (
                    <>
                        <p className="text-slate-700 mb-8" data-testid="referral-landing-success">{t("referral.success")}</p>
                        <button onClick={() => nav("/dashboard")} className="btn-primary w-full justify-center" data-testid="referral-landing-go-btn">
                            {t("referral.go_dashboard")}
                        </button>
                    </>
                )}

                {status === "error" && (
                    <p className="text-slate-500" data-testid="referral-landing-error">{error}</p>
                )}
            </div>
        </div>
    );
}
